import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { api } from '../services/api';
import { toast, confirm as swalConfirm } from '../utils/swal';
import AppHeader from './AppHeader';
import './VenuePhotoUpload.css'; // Aynı stil kullanılıyor

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const EventPhotoUpload = ({ event, onBack, onUpdate, onGoToProfile, onGoToEventManagement, onGoToAdminPanel, onGoToVenueManage, onGoToQrScanner }) => {
  const [mainPhoto, setMainPhoto]         = useState(event.mainPhoto || null);
  const [galleryPhotos, setGalleryPhotos] = useState(event.galleryPhotos || []);
  const [uploading, setUploading]         = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');

  const notifyUpdate = (main, gallery) => {
    onUpdate?.({ ...event, mainPhoto: main, galleryPhotos: gallery });
  };

  const onDropMain = useCallback(async (acceptedFiles) => {
    if (!acceptedFiles.length) return;
    setUploading(true);
    setUploadProgress('Ana fotoğraf yükleniyor...');
    try {
      const result = await api.uploadEventPhoto(event.id, acceptedFiles[0], true);
      setMainPhoto(result.photoUrl);
      notifyUpdate(result.photoUrl, galleryPhotos);
      toast.success('Ana fotoğraf yüklendi!');
    } catch (err) {
      toast.error('Fotoğraf yüklenemedi: ' + err.message);
    } finally {
      setUploading(false);
      setUploadProgress('');
    }
  }, [event, galleryPhotos]);

  const onDropGallery = useCallback(async (acceptedFiles) => {
    if (!acceptedFiles.length) return;
    setUploading(true);
    setUploadProgress(`${acceptedFiles.length} fotoğraf yükleniyor...`);
    try {
      const results  = await Promise.all(acceptedFiles.map(f => api.uploadEventPhoto(event.id, f, false)));
      const newPhotos = results.map(r => r.photoUrl);
      const updated   = [...galleryPhotos, ...newPhotos];
      setGalleryPhotos(updated);
      notifyUpdate(mainPhoto, updated);
      toast.success('Fotoğraflar yüklendi!');
    } catch (err) {
      toast.error('Yükleme başarısız: ' + err.message);
    } finally {
      setUploading(false);
      setUploadProgress('');
    }
  }, [event, mainPhoto, galleryPhotos]);

  const handleRemoveMain = async () => {
    if (!(await swalConfirm.delete('ana fotoğrafı')).isConfirmed) return;
    try {
      await api.deleteEventPhoto(event.id, mainPhoto);
      setMainPhoto(null);
      notifyUpdate(null, galleryPhotos);
    } catch { toast.error('Silinemedi!'); }
  };

  const handleRemoveGallery = async (photoUrl) => {
    if (!(await swalConfirm.delete('bu fotoğrafı')).isConfirmed) return;
    try {
      await api.deleteEventPhoto(event.id, photoUrl);
      const updated = galleryPhotos.filter(p => p !== photoUrl);
      setGalleryPhotos(updated);
      notifyUpdate(mainPhoto, updated);
    } catch { toast.error('Silinemedi!'); }
  };

  const { getRootProps: getMainRootProps, getInputProps: getMainInputProps, isDragActive: isMainDrag } = useDropzone({
    onDrop: onDropMain, accept: { 'image/*': ['.jpeg', '.jpg', '.png', '.webp'] }, maxFiles: 1, disabled: uploading,
  });

  const { getRootProps: getGalleryRootProps, getInputProps: getGalleryInputProps, isDragActive: isGalleryDrag } = useDropzone({
    onDrop: onDropGallery, accept: { 'image/*': ['.jpeg', '.jpg', '.png', '.webp'] }, multiple: true, disabled: uploading,
  });

  return (
    <div className="photo-upload-container">
      <AppHeader
        title={`📸 ${event.name} — Fotoğraflar`}
        onBack={onBack}
        backLabel="← Etkinlik Listesi"
        onGoToProfile={onGoToProfile}
        onGoToEventManagement={onGoToEventManagement}
        onGoToAdminPanel={onGoToAdminPanel}
        onGoToVenueManage={onGoToVenueManage}
        onGoToQrScanner={onGoToQrScanner}
      />

      {uploading && (
        <div className="upload-progress">
          <div className="progress-spinner" />
          <p>{uploadProgress}</p>
        </div>
      )}

      {/* Ana Fotoğraf */}
      <div className="photo-section">
        <h3>🖼️ Ana Fotoğraf</h3>
        <p className="section-description">Etkinlik kartlarında ve ana sayfada gösterilecek fotoğraf</p>
        <div className="main-photo-area">
          {mainPhoto ? (
            <div className="main-photo-preview">
              <img src={`${API_BASE_URL}${mainPhoto}`} alt="Ana fotoğraf" className="main-photo-img" />
              <div className="photo-overlay">
                <button {...getMainRootProps()} className="change-photo-btn" disabled={uploading}>
                  <input {...getMainInputProps()} />
                  🔄 Değiştir
                </button>
                <button onClick={handleRemoveMain} className="remove-main-btn" disabled={uploading}>🗑️ Sil</button>
              </div>
            </div>
          ) : (
            <div {...getMainRootProps()} className={`dropzone main-dropzone ${isMainDrag ? 'active' : ''} ${uploading ? 'disabled' : ''}`}>
              <input {...getMainInputProps()} />
              <div className="dropzone-content">
                <div className="dropzone-icon">📷</div>
                {isMainDrag ? <p>Bırakın...</p> : <><p>Ana fotoğrafı sürükleyip bırakın</p><p className="dropzone-hint">veya tıklayarak seçin</p></>}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Galeri */}
      <div className="photo-section">
        <h3>🎨 Galeri Fotoğrafları ({galleryPhotos.length})</h3>
        <p className="section-description">Etkinlik detay sayfasında gösterilecek ek fotoğraflar</p>
        <div {...getGalleryRootProps()} className={`dropzone gallery-dropzone ${isGalleryDrag ? 'active' : ''} ${uploading ? 'disabled' : ''}`}>
          <input {...getGalleryInputProps()} />
          <div className="dropzone-content">
            <div className="dropzone-icon">🖼️</div>
            {isGalleryDrag ? <p>Bırakın...</p> : <><p>Galeri fotoğraflarını sürükleyip bırakın</p><p className="dropzone-hint">veya tıklayarak seçin</p></>}
          </div>
        </div>
        {galleryPhotos.length > 0 && (
          <div className="gallery-grid">
            {galleryPhotos.map((photo, i) => (
              <div key={i} className="gallery-item">
                <img src={`${API_BASE_URL}${photo}`} alt={`Galeri ${i + 1}`} className="gallery-img" />
                <button onClick={() => handleRemoveGallery(photo)} className="remove-photo-btn" disabled={uploading}>✕</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default EventPhotoUpload;
